import { featureContainer } from '@payfit/growth-platform-brand-system-v2';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import { blockSpacingConfig, defaultSpacing } from './spacingConfig';
import { SpacingPreset } from './types';

/**
 * Determines if two blocks should share the same background color
 * Used to adjust spacing between blocks with similar backgrounds
 *
 * @param blockA - First block to compare
 * @param blockB - Second block to compare
 * @returns true if blocks should share background, false otherwise
 */
const blocksHasSameBackground = (blockA: any, blockB: any) => {
  let backgroundA = blockA?.backgroundColor;
  let backgroundB = blockB?.backgroundColor;
  if (!blockA?.backgroundColor || blockA?.isEncapsulated) backgroundA = 'white';
  if (!blockB?.backgroundColor || blockB?.isEncapsulated) backgroundB = 'white';

  if (blockA?.__typename === 'DatoCmsSeparator') return true;
  if (blockB?.__typename === 'DatoCmsSeparator') return true;
  return backgroundA === backgroundB;
};

/**
 * Determines the HTML element to use for specific block types
 *
 * @param props - Block properties containing __typename and as
 * @returns HTML element tag to use for the block
 */
const changeAs = ({ as, __typename }: any) => {
  if (__typename === 'DatoCmsSeparator') return 'div';
  if ('DatoCmsHero' === __typename) return 'div';
  return as;
};

/**
 * Gets spacing configuration for a block based on its context
 * Handles both preset and custom spacing values
 *
 * @param block - The block to get spacing for
 * @param blocks - All blocks in the current template (used for context)
 * @param pageId - ID of the current page
 * @param index - Index of the current block
 * @returns SpacingPreset containing top and bottom spacing values
 *
 * @example
 * const spacing = getBlockSpacing({
 *   block: { __typename: 'DatoCmsFaq' },
 *   blocks: allBlocks,
 *   pageId: 'DatoCmsGenericPage',
 *   index: 0
 * });
 */
const getBlockSpacing = ({
  block,
  blocks,
  pageId,
  index,
  doc,
}: {
  block: any;
  blocks: any[];
  pageId: string;
  index: number;
  doc: any;
}): SpacingPreset => {
  const blockType = block.__typename;
  const config = blockSpacingConfig[blockType];

  if (!config) return defaultSpacing;

  // Check contextual rules in order of specificity
  if (config.contextual) {
    // Check page-specific rules
    if (config.contextual.specific?.[pageId]) {
      return config.contextual.specific[pageId];
    }

    // Check first/last block rules
    if (index === 0 && config.contextual.firstBlock) {
      return config.contextual.firstBlock;
    }

    if (index === blocks.length - 1 && config.contextual.lastBlock) {
      return config.contextual.lastBlock;
    }

    // Check adjacent blocks with same background
    if (
      config.contextual.adjacentSame &&
      blocksHasSameBackground(blocks[index - 1], block)
    ) {
      return config.contextual.adjacentSame;
    }
  }

  // Return default config for this block type
  return config.default;
};

/**
 * Determines if a specific code color should be forced for the block
 */
const getForceCodeColor = ({ __typename, isDark }: any) => {
  if (__typename === 'DatoCmsKeyFigure' && !isDark) return 51;
  return;
};

/**
 * Determines if a block should be contained within max-width
 */
const getIsContained = ({ __typename }: any) => {
  if (__typename === 'DatoCmsAnnualLeaveCalculator') return true;
  if (__typename === 'DatoCmsConversionBanner') return false;
  if (__typename === 'DatoCmsFeaturesList') return true;
  if (__typename === 'DatoCmsBenefitsSection') return true;
  if (__typename === 'DatoCmsVersusSection') return true;
  return __typename === 'DatoCmsAskADemoLink';
};

/**
 * Determines the background color for a block based on its type and context
 */
const getBackgroundColor = ({
  pageId,
  __typename,
  backgroundColor,
  isEncapsulated,
  key,
  doc,
}: any) => {
  if (
    [
      'DatoCmsVideoModule',
      'DatoCmsEmbedArcade',
      'DatoCmsLogoListStatic',
    ]?.includes(__typename) &&
    key === 0
  )
    return 'transparent';

  if (
    ['DatoCmsLogoList', 'DatoCmsAskADemoLink'].includes(__typename) &&
    pageId === 'DatoCmsPricingPageV3'
  )
    return 'blue';

  if (
    __typename === 'DatoCmsNavigationButtonsList' &&
    pageId === 'DatoCmsPillarPage'
  )
    return 'neutral';

  if (__typename === 'DatoCmsSimpleCardsCarousel') {
    if (backgroundColor === 'grey') return 'grey';
  }

  return backgroundColor && !isEncapsulated && backgroundColor;
};

const getIsDark = ({ isDark }: any) => {
  return isDark;
};

/**
 * Applies specific className configurations for certain block types
 */
const getClassName = ({ key, __typename, pageId, hero }: any) => {
  const normalClassNames = classNames(
    'relative mx-16 w-auto',
    'sm:mx-24',
    'lg:px-[107px]',
    'lg:mx-[100px] xxl:mx-auto xl:max-w-[1240px]',
  );
  const classNamesAfterHero = classNames(
    'relative mx-16 px-24 w-auto',
    'md:mx-24 md:px-32',
    'lg:px-[107px]',
    'lg:mx-[100px] xxl:mx-auto xl:max-w-[1240px]',
  );

  if (
    ['DatoCmsVideoModule', 'DatoCmsImageBlock', 'DatoCmsEmbedArcade']?.includes(
      __typename,
    )
  ) {
    /** isFeatures Page */
    if (
      ['DatoCmsHeroMinimal'].includes(hero?.__typename)
    )
      return featureContainer;
    /**
    * isWhyPayfitPage page and isImageBlock block
    * Force full-width on imageBlock from whyPayfit page
    * No container needed
    **/
    if (pageId === 'DatoCmsWhyPayfitPage' && __typename === 'DatoCmsImageBlock') {
      return 'relative w-auto';
    }
    /** Other pages */
    if (key === 0) return classNamesAfterHero;

    return normalClassNames;
  }
};

/**
 * Prepares all necessary props for a Section component based on block type and context
 *
 * @param block - The block to prepare props for
 * @param blocks - All blocks in the current template (used for context)
 * @param pageId - ID of the current page
 * @param key - Index of the current block
 * @param doc - The current page data
 * @returns Object containing all necessary Section component props
 *
 * @example
 * const sectionProps = prepareSectionProps({
 *   block: { __typename: 'DatoCmsFaq' },
 *   blocks: allBlocks,
 *   pageId: 'DatoCmsGenericPage',
 *   key: 0
 * });
 *
 * // Returns
 * {
 *   as: 'div',
 *   isContained: false,
 *   topPadding: 'half',
 *   bottomPadding: 'half',
 *   backgroundColor: 'white',
 *   forceCodeColor: undefined,
 *   isDark: false,
 *   className: undefined
 * }
 */
export const prepareSectionProps = ({
  block,
  blocks,
  pageId,
  key,
  doc,
}: any) => {
  const hero = doc?.hero?.[0] || doc?.hero;
  const spacing = getBlockSpacing({
    block,
    blocks,
    pageId,
    index: key,
    doc,
  });

  const backgroundColor = getBackgroundColor({ ...block, pageId, key, doc });
  const forceCodeColor = getForceCodeColor({ ...block, pageId });

  const as = changeAs({ ...block, pageId });

  const isDark = getIsDark({ ...block, pageId, key });
  const isContained = getIsContained({ ...block, key });
  const className = getClassName({ ...block, key, pageId, hero });

  return {
    as,
    isContained,
    topPadding: spacing.top,
    bottomPadding: spacing.bottom,
    backgroundColor,
    forceCodeColor,
    isDark,
    className,
  };
};
